import React, { ElementType, ReactElement, RefObject, useEffect, useMemo, useState } from 'react';

import { GiftIcon } from '@heroicons/react/24/solid';

import Stepper from '@/components/stepper/Stepper';
import Step from '@/components/stepper/Step';
import BasicInfoForm from '@/features/modal/modals/register/ontario/BasicInfoForm';
import AddressInfoForm from '@/features/modal/modals/register/ontario/AddressInfoForm';
import WorkInfoForm from '@/features/modal/modals/register/ontario/WorkInfoForm';
import ConsentsForm from '@/features/modal/modals/register/ontario/ConsentsForm';
import RegistrationUsp from '@/features/modal/modals/register/ontario/RegistrationUsp';
import RegistrationCustomerSupport from '@/features/modal/modals/register/ontario/RegistrationCustomerSupport';
import { useIntl } from 'react-intl';
import classNames from 'classnames';
import ClosePrompt from '@/features/modal/modals/register/ontario/ClosePrompt';
import WelcomeOfferBanner from '@/features/modal/modals/register/ontario/WelcomeOfferBanner';

interface Steps {
  formElement: ReactElement;
  stepIcon?: ElementType;
}

const OntarioRegistration = ({
  closeModal,
  modalWrapperRef,
}: {
  closeModal: VoidFunction;
  modalWrapperRef: RefObject<HTMLDivElement>;
}) => {
  const [activeStep, setActiveStep] = useState(1);
  // showError is for special error handling when we want to get out of the stepper but still show the error
  const [showError, setShowError] = useState<string | null>(null);
  // This state is used to keep the user on the registration
  const [showClosePrompt, setShowClosePrompt] = useState(false);
  const intl = useIntl();

  const stepBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const stepForward = () => {
    setActiveStep(prev => prev + 1);
  };

  const displayClosePrompt = () => {
    if (showClosePrompt || activeStep === 1) {
      closeModal();
    } else {
      setShowClosePrompt(true);
    }
  };

  const removeClosePrompt = () => {
    setShowClosePrompt(false);
  };

  const steps: Steps[] = [
    { formElement: <BasicInfoForm closeModal={closeModal} stepForward={stepForward} /> },
    { formElement: <AddressInfoForm stepBack={stepBack} stepForward={stepForward} /> },
    { formElement: <WorkInfoForm stepBack={stepBack} stepForward={stepForward} /> },
    {
      formElement: (
        <ConsentsForm stepBack={stepBack} setShowError={setShowError} closeModal={closeModal} />
      ),
      stepIcon: GiftIcon,
    },
  ];

  const renderElement = useMemo(() => {
    if (showError) {
      return <RegistrationCustomerSupport closeModal={closeModal} />;
    } else {
      return (
        <>
          <div ref={modalWrapperRef}>
            <RegistrationUsp onClose={displayClosePrompt} />
          </div>
          <div className="p-5 pb-8 sm:p-8 sm:pb-10">
            <WelcomeOfferBanner />
            <div className={classNames('hidden', { '!block': showClosePrompt })}>
              <ClosePrompt closeModal={closeModal} continueRegistration={removeClosePrompt} />
            </div>
            <div className={classNames('block', { '!hidden': showClosePrompt })}>
              <Stepper activeStep={activeStep}>
                {steps.map((step, index) => (
                  <Step stepIcon={step.stepIcon} key={index}>
                    {step.formElement}
                  </Step>
                ))}
              </Stepper>
              <p
                data-cy="ontario-register-exit-modal-button"
                className="mb-0 mt-4 text-[15px] text-white text-center cursor-pointer mx-auto w-fit"
                onClick={displayClosePrompt}
              >
                {intl.formatMessage({ id: 'registration.ontario.modalExit.cta' })}
              </p>
            </div>
          </div>
        </>
      );
    }
  }, [showClosePrompt, showError, activeStep, steps]);

  /*
   * This is used to position the user on the top beginning of every step when navigating between steps
   * */
  useEffect(() => {
    if (modalWrapperRef && modalWrapperRef.current) {
      modalWrapperRef.current.scrollTo(0, modalWrapperRef.current.offsetTop - 16);
    }
  }, [activeStep]);

  return (
    <div
      className="bg-white/10 rounded-lg backdrop-blur-md shadow-md border-white/10 border-solid
      border-[1px] max-w-xl w-full"
    >
      {renderElement}
    </div>
  );
};

export default OntarioRegistration;
